import * as React from 'react'
import {Box, Typography, Chip, Button, Tooltip} from '@mui/material'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import UploadOutlinedIcon from '@mui/icons-material/UploadOutlined'
import AcUnitOutlinedIcon from '@mui/icons-material/AcUnitOutlined'
import {TextField} from '@mui/material'
import {sectionCardSx, alertStripSx, primaryBtnSx, secondaryBtnSx} from '../../../../styles/patterns'
import {FONT, SIZE, WEIGHT} from '../../../../styles/foundation/typography'
import {NAVY, SUBTLE, BORDER, WARNING, INFO, ERROR, PURPLE} from '../../../../styles/foundation/colors'
import {GAP} from '../../../../styles/foundation/spacing'
import {FREEZE_LEVEL_LABEL, APPROVAL_NOTICE} from '../data/reference'
import {date, int, kg} from '../format'
import {
  stateMeta, latestInfoRequest, submissionsUsed, submissionsLeft, canSubmit, isProposal, MAX_SUBMISSIONS,
} from './logic'
import {EXHAUSTED_LINE, BLOCKED_REASON, BUDGET_OPEN_LINE, BUDGET_HELD_LINE} from './copy'
import {chipSentenceSx} from '../components/tone'
import KeyValue from '../components/KeyValue'
import RqLevelChip from './RqLevelChip'
import RqSection from './RqSection'
import RqWeights from './RqWeights'
import RqCounter from './RqCounter'
import RqTimeline from './RqTimeline'
import RqSubmitDrawer from './RqSubmitDrawer'

// Caption/helper tier — parity with disputes-v2 (sdd/type-parity-spec.md).
const bannerTextSx = {fontFamily: FONT, fontSize: SIZE['2xs'], lineHeight: 1.6, fontWeight: WEIGHT.regular}
const metaTextSx = {fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE}

/** Icon per review sub-state `key` (see logic.js's stateMeta) — never per prose. */
const STATE_ICON = {awaiting: AccessTimeIcon, additional: HelpOutlineIcon, submitted: UploadOutlinedIcon, proposed: AcUnitOutlinedIcon}

/**
 * The merchant's OWN capture, counted for the details grid — a companion to
 * `evidence` (scanner records on file) and `evidenceAwbs` (the shipments those
 * records belong to), never folded into either.
 *
 * Counted here rather than in requests/logic.js because this is the only screen
 * that shows it: `RqRejectCard` and `RqApprovedDrawer` render seeded requests
 * exclusively — nothing in the module ever writes `kind: 'rejected'` or
 * `'approved'`, and `applySubmission` leaves `kind` alone — and no seed carries
 * `evidenceFiles`, so a row there would be a permanent em dash.
 *
 * Deliberately NOT the same wording as initiate/request.js's `raisedWith`
 * ('a packing video and 2 photographs'): that builds a sentence, this fills a
 * right-aligned grid cell, and the two would fight over one phrase table.
 */
function uploadedWithIt(req) {
  const files = req.evidenceFiles || {}
  const shots = (files.images || []).length
  const parts = [
    files.video ? '1 video' : '',
    shots ? int(shots) + (shots === 1 ? ' photo' : ' photos') : '',
  ].filter(Boolean)
  return parts.length ? parts.join(' + ') : '—'
}

/**
 * One state banner, chosen off `stateMeta(req.state).key`. `additional`
 * reads `event.requestedItems` straight off `latestInfoRequest` — never a
 * stripped string — and degrades gracefully (bold line + closing sentence,
 * no chips) if a backend ever records the ADDITIONAL state without the
 * event itself.
 */
function StateBanner({req, metaKey}) {
  if (metaKey === 'awaiting') {
    return (
      <Box sx={{...alertStripSx(WARNING), alignItems: 'flex-start'}}>
        <Typography sx={bannerTextSx}>
          <Box component='span' sx={{fontWeight: WEIGHT.bold}}>With Prozo for review.</Box>{' '}
          Nothing has been asked for, so there is nothing to send yet. If Prozo needs more, it asks
          here and names what it wants.
        </Typography>
      </Box>
    )
  }

  if (metaKey === 'additional') {
    const ev = latestInfoRequest(req.history)
    const items = ev && Array.isArray(ev.requestedItems) ? ev.requestedItems : []
    return (
      <Box sx={{...alertStripSx(INFO), alignItems: 'flex-start'}}>
        <Box sx={{display: 'grid', gap: GAP.xs}}>
          <Typography sx={bannerTextSx}>
            <Box component='span' sx={{fontWeight: WEIGHT.bold}}>Prozo has asked for more data.</Box>
          </Typography>
          {items.length > 0 && (
            <Box sx={{display: 'flex', alignItems: 'baseline', gap: GAP.xs, flexWrap: 'wrap'}}>
              <Typography sx={{...bannerTextSx, fontWeight: WEIGHT.semibold}}>Requested:</Typography>
              {items.map((item, i) => (
                <Chip key={item + i} size='small' label={item} sx={chipSentenceSx('info')} />
              ))}
            </Box>
          )}
          {ev && <Typography sx={bannerTextSx}>Asked on {date(ev.date)}.</Typography>}
          <Typography sx={bannerTextSx}>
            Sending {items.length > 0 ? 'these' : 'what was asked for'} spends one of the{' '}
            {submissionsLeft(req)} remaining submissions. This request does not move until you respond.
          </Typography>
        </Box>
      </Box>
    )
  }

  if (metaKey === 'submitted') {
    return (
      <Box sx={{...alertStripSx(INFO), alignItems: 'flex-start'}}>
        <Typography sx={bannerTextSx}>
          <Box component='span' sx={{fontWeight: WEIGHT.bold}}>Your data is in and back with Prozo.</Box>{' '}
          Nothing is needed from you, and nothing further can be sent unless Prozo asks again. Prozo will
          either approve the freeze or decline it, and the decision lands against round {req.round || 1}.
        </Typography>
      </Box>
    )
  }

  // A Prozo auto-proposal (T12/T13): the merchant decides, not Prozo. The basis is stated in
  // the merchant's own terms — how many of THEIR accepted discrepancies it rests on, and the
  // median — and the T23 notice says what accepting does and does not change.
  if (metaKey === 'proposed') {
    const basis = req.proposalBasis || {}
    return (
      <Box sx={{...alertStripSx(PURPLE), alignItems: 'flex-start'}}>
        <Box sx={{display: 'grid', gap: GAP.xs}}>
          <Typography sx={bannerTextSx}>
            <Box component='span' sx={{fontWeight: WEIGHT.bold}}>Prozo has proposed a weight freeze of {kg(req.chargeKg)} for this SKU.</Box>
          </Typography>
          {basis.acceptedCount > 0 && (
            <Typography sx={bannerTextSx}>
              It is the median of the {int(basis.acceptedCount)} courier weights you accepted on this SKU
              {basis.acceptedWeightsGm && basis.acceptedWeightsGm.length
                ? ' (' + basis.acceptedWeightsGm.map((g) => (g / 1000).toFixed(2)).join(', ') + ' kg)'
                : ''}
              .
            </Typography>
          )}
          <Typography sx={bannerTextSx}>
            Accepting fixes this SKU's billed weight at {kg(req.chargeKg)}. {APPROVAL_NOTICE} Declining leaves things as they are
            and Prozo will not propose this SKU again for a while.
          </Typography>
        </Box>
      </Box>
    )
  }

  // A backend that renames REVIEW_STATE without updating stateMeta's lookup
  // degrades to no banner rather than crashing or rendering a fabricated one.
  return null
}

/**
 * Weight Freeze (v2) — Requests: one under-review request.
 *
 * A card, not a table row — a state banner, a weights triple, a fact block,
 * a submission counter, a full round-by-round timeline and two actions is
 * more than a single row can carry legibly. The card owns only its own
 * `drawerOpen`; the actual submission goes up via `onSubmit(req, payload)` —
 * this component never touches the shared store.
 *
 * LAYOUT. Header band, then the state banner, then two columns, then the
 * action footer. The columns are what stop the card sprawling: everything
 * about the SHIPMENT (its three weights, then what was requested against it)
 * runs down the left, everything about the REVIEW (how much of the budget is
 * spent, then the round-by-round record) runs down the right. Both stacks
 * are tall, so neither leaves the dead rectangle the previous
 * facts-beside-counter split did, and no block is left spanning the full
 * ~1250px of the card with a right-aligned value at the far edge of it.
 */
export default function RqReviewCard({req, pack, onSubmit, onAcceptProposal, onDeclineProposal, onNavigate, focusFallbackRef}) {
  const [drawerOpen, setDrawerOpen] = React.useState(false)
  // The decline half of a Prozo proposal asks for an optional one-line reason first.
  const [declineOpen, setDeclineOpen] = React.useState(false)
  const [declineNote, setDeclineNote] = React.useState('')
  const proposal = isProposal(req)

  // Same focus-recovery fallback every other drawer in this module reuses
  // (see catalogue/CataloguePage.js's closeMergeDrawer): sending flips the
  // request out of "additional data requested", so the trigger that opened
  // this drawer is `disabled` by the time it closes and cannot receive MUI's
  // own focus restore — the same "trigger vanished" case those drawers guard.
  const closeDrawer = React.useCallback(() => {
    setDrawerOpen(false)
    if (typeof window === 'undefined' || typeof document === 'undefined') return
    window.requestAnimationFrame(() => {
      if (document.activeElement === document.body && focusFallbackRef && focusFallbackRef.current) {
        focusFallbackRef.current.focusActive()
      }
    })
  }, [focusFallbackRef])

  const handleSubmitPayload = React.useCallback((payload) => {
    onSubmit(req, payload)
    closeDrawer()
  }, [onSubmit, req, closeDrawer])

  const meta = stateMeta(req.state)
  const StateIcon = STATE_ICON[meta.key] || null
  const evidenceAwbCount = (req.evidenceAwbs || []).length

  // Two different unavailabilities with two different sentences: a spent
  // budget closes the request for good, an un-asked-for round is merely not
  // yet open. The budget reads first because it is the harder truth.
  const blocked = !canSubmit(req)
  const spent = submissionsLeft(req) === 0
  const blockedReason = !blocked ? ''
    : (spent ? BLOCKED_REASON.exhausted : BLOCKED_REASON[meta.key] || BLOCKED_REASON.awaiting)

  const detailItems = [
    {label: 'Level', value: FREEZE_LEVEL_LABEL[req.level] || req.level},
    {label: 'Packing material', value: pack ? pack.label : 'Not set'},
    {label: 'Its volumetric weight', value: pack && pack.volKg != null ? kg(pack.volKg) : '—'},
    {label: 'DWS images', value: int(req.evidence || 0)},
    {label: 'Attached records', value: int(evidenceAwbCount)},
    // The third source, and the only one a merchant can still add. Without it
    // this grid reads 'DWS images 0 / Attached records 0' on every request
    // raised since the record picker went, directly beside a timeline saying a
    // packing video and two photographs were uploaded with it — the two zeros
    // are each accurate and the block as a whole was misleading. Same em-dash
    // convention as 'Its volumetric weight' above for the genuinely absent
    // case, which is what every SEEDED request shows: they predate in-drawer
    // capture and carry no `evidenceFiles` at all.
    {label: 'Uploaded with it', value: uploadedWithIt(req)},
  ]
  if (req.remarks) detailItems.push({label: 'Remarks', value: req.remarks, full: true})

  return (
    <Box sx={sectionCardSx}>
      <Box sx={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
        gap: GAP.sm, flexWrap: 'wrap', pb: GAP.sm, borderBottom: `1px solid ${BORDER}`,
      }}>
        <Box sx={{minWidth: 0, flex: '1 1 320px'}}>
          <Typography component='h3' sx={{fontFamily: FONT, fontWeight: WEIGHT.bold, fontSize: SIZE.md, color: NAVY, m: 0}}>
            {req.subject}
          </Typography>
          <Box sx={{display: 'flex', alignItems: 'center', gap: GAP.xs, flexWrap: 'wrap', mt: 0.35}}>
            <Typography sx={{fontFamily: 'monospace', fontSize: SIZE.xs, color: SUBTLE}}>{req.id}</Typography>
            <RqLevelChip level={req.level} />
          </Box>
          {/* The count clause only appears when there IS a count. Nothing in the
              UI can put a DWS record on a newly raised request any more (see
              initiate/request.js's raisedWith), so a kept "· 0 DWS images
              attached" would print on every request the merchant raises — in
              the header, where it reads as a deficiency notice rather than as
              the fact that this particular request carries no records. The
              seeded requests that do carry some still say so.
              The singular is not cosmetic: two seeds hold exactly 1
              (WF-NB-2601, WF-NB-2610), and this line used to render "1 DWS
              images attached" while `raisedWith` got the same case right — the
              module disagreeing with itself about one number.
              The 'DWS images' row in the details grid below is a different case
              and deliberately keeps its zero: it is a labelled data cell, not a
              headline, and it now sits beside an 'Uploaded with it' row so the
              block accounts for all three sources rather than showing two zeros
              next to a timeline that names a video. RqSubmitDrawer's helper text
              is the one place left that states this count in a full sentence;
              it is not covered by either decision here. */}
          <Typography sx={{...metaTextSx, mt: 0.25}}>
            Raised by {req.raisedBy} on {date(req.raisedOn)}
            {req.evidence > 0
              && ` · ${int(req.evidence)} DWS ${req.evidence === 1 ? 'image' : 'images'} attached`}
          </Typography>
        </Box>
        {/* `maxWidth` matters: 'Data submitted — awaiting decision' is a wide
            chip, and without a cap a `flexShrink: 0` badge row runs past the
            card edge once it wraps onto its own line. */}
        <Box sx={{display: 'flex', gap: GAP.xs, alignItems: 'center', flexWrap: 'wrap', flexShrink: 0, maxWidth: '100%'}}>
          <Chip
            size='small'
            label={`Round ${req.round || 1} of up to ${MAX_SUBMISSIONS}`}
            sx={chipSentenceSx('neutral')}
          />
          <Chip
            size='small'
            icon={StateIcon ? <StateIcon sx={{fontSize: 14}} /> : undefined}
            label={req.state}
            sx={{...chipSentenceSx(meta.tone), '& .MuiChip-icon': {color: 'inherit', fontSize: 14}}}
          />
        </Box>
      </Box>

      <Box sx={{mt: GAP.md}}>
        <StateBanner req={req} metaKey={meta.key} />
      </Box>

      <Box sx={{
        display: 'grid', gap: GAP.lg, mt: GAP.md,
        gridTemplateColumns: {xs: 'minmax(0, 1fr)', lg: 'minmax(0, 7fr) minmax(0, 5fr)'},
      }}>
        <Box sx={{display: 'grid', gap: GAP.md, alignContent: 'start', minWidth: 0}}>
          <RqSection title='Shipment weight'>
            <RqWeights req={req} compact />
          </RqSection>
          <RqSection title='Request details'>
            <KeyValue items={detailItems} columns={2} />
          </RqSection>
        </Box>

        <Box sx={{display: 'grid', gap: GAP.md, alignContent: 'start', minWidth: 0}}>
          <RqSection title='Review progress'>
            <RqCounter used={submissionsUsed(req)} round={req.round} />
          </RqSection>
          <RqTimeline history={req.history} round={req.round} label='Round by round' />
        </Box>
      </Box>

      <Box sx={{
        display: 'flex', alignItems: 'center', gap: GAP.md, flexWrap: 'wrap',
        mt: GAP.md, pt: GAP.md, borderTop: `1px solid ${BORDER}`,
      }}>
        {proposal ? (
          // A Prozo proposal has no submission budget to spend — the merchant's two answers are
          // accept or decline, and that is the whole footer.
          <>
            <Button sx={primaryBtnSx} onClick={() => onAcceptProposal && onAcceptProposal(req)}>Accept this weight</Button>
            {!declineOpen && (
              <Button sx={secondaryBtnSx} onClick={() => setDeclineOpen(true)}>Decline</Button>
            )}
            {declineOpen && (
              <Box sx={{display: 'flex', gap: GAP.sm, alignItems: 'center', flexWrap: 'wrap', flex: '1 1 320px'}}>
                <TextField
                  size='small'
                  value={declineNote}
                  onChange={(event) => setDeclineNote(event.target.value)}
                  placeholder='Why (optional) — for example: moving to a smaller carton'
                  inputProps={{'aria-label': 'Reason for declining the proposal'}}
                  sx={{fontFamily: FONT, fontSize: SIZE['2xs'], minWidth: 260, flex: '1 1 260px'}}
                />
                <Button sx={secondaryBtnSx} onClick={() => { onDeclineProposal && onDeclineProposal(req, declineNote.trim()); setDeclineOpen(false) }}>
                  Confirm decline
                </Button>
                <Button sx={secondaryBtnSx} onClick={() => setDeclineOpen(false)}>Keep it open</Button>
              </Box>
            )}
            <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], lineHeight: 1.5, color: SUBTLE, flex: '1 1 240px', minWidth: 0}}>
              Your decision, not Prozo's. Nothing bills until you accept and the new weight rule is switched on for your account.
            </Typography>
          </>
        ) : (
          <>
            {blocked ? (
              // Same explained-disabled pattern as initiate's own submit gate: the
              // reason rides a focusable span, because a disabled button takes no
              // focus and browsers do not surface its title reliably.
              <Tooltip title={blockedReason}>
                <Box
                  component='span' tabIndex={0} role='note'
                  aria-label={'Submit is unavailable. ' + blockedReason}
                  sx={{display: 'inline-flex'}}
                >
                  <Button sx={primaryBtnSx} disabled>Submit additional data</Button>
                </Box>
              </Tooltip>
            ) : (
              <Button sx={primaryBtnSx} onClick={() => setDrawerOpen(true)}>Submit additional data</Button>
            )}
            <Button sx={secondaryBtnSx} onClick={() => onNavigate('dws')}>Open DWS images</Button>
            {/* Next to the buttons, not flung to the far edge by `ml: auto` — it is
                the sentence explaining why the submit control is in the state it
                is in, so it has to sit within reading distance of it. */}
            <Typography sx={{
              fontFamily: FONT, fontSize: SIZE['2xs'], lineHeight: 1.5,
              color: spent ? ERROR : SUBTLE, flex: '1 1 240px', minWidth: 0,
            }}>
              {spent ? EXHAUSTED_LINE
                : (blocked ? BUDGET_HELD_LINE(submissionsLeft(req)) : BUDGET_OPEN_LINE(submissionsLeft(req)))}
            </Typography>
          </>
        )}
      </Box>

      <RqSubmitDrawer req={req} open={drawerOpen} onClose={closeDrawer} onSubmit={handleSubmitPayload} />
    </Box>
  )
}
